/** E9: SEPA, Mahnwesen, Einbehalt, Auswertungen, Regal-Leistung – Hooks für die Verwaltung. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { laeufeListe, lastschriftKandidaten, mandateListe, ueberweisungKandidaten } from "../../worker/data/sepa";
import type { mahnungenListe } from "../../worker/data/mahnwesen";
import type { auswertung, regalLeistung } from "../../worker/data/auswertungen";

export type SepaDaten = { laeufe: Awaited<ReturnType<typeof laeufeListe>>; ueberweisungen: Awaited<ReturnType<typeof ueberweisungKandidaten>>; lastschriften: Awaited<ReturnType<typeof lastschriftKandidaten>>; mandate: Awaited<ReturnType<typeof mandateListe>> };
export type Mahnung = Awaited<ReturnType<typeof mahnungenListe>>[number];
export type Auswertung = Awaited<ReturnType<typeof auswertung>>;
export type RegalLeistung = Awaited<ReturnType<typeof regalLeistung>>;

function nach() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["laden"] });
}

export const useSepa = () => useQuery({ queryKey: ["laden", "sepa"], queryFn: () => rufe<SepaDaten>(api.api.laden.sepa.$get()) });
export const useMahnungen = () => useQuery({ queryKey: ["laden", "mahnungen"], queryFn: () => rufe<{ mahnungen: Mahnung[] }>(api.api.laden.mahnungen.$get()) });
export const useAuswertung = (von: string, bis: string) => useQuery({ queryKey: ["laden", "auswertung", von, bis], queryFn: () => rufe<Auswertung>(api.api.laden.auswertungen.$get({ query: { von, bis } })), enabled: !!von && !!bis });
export const useRegalLeistung = (id: string) => useQuery({ queryKey: ["laden", "regale", id, "leistung"], queryFn: () => rufe<RegalLeistung>(api.api.laden.regale[":id"].leistung.$get({ param: { id } })), enabled: !!id });

type SepaErgebnis = { id?: string; anzahl?: number; summeCent?: number; einzugTag?: string; bezahlt?: number; uebersprungen?: { nummer: string; grund: string }[] };
export function useSepaAktion() {
  const n = nach();
  return useMutation({
    mutationFn: async (v: { art: "ueberweisung" | "lastschrift"; ids?: string[] } | { art: "eingezogen"; id: string } | { art: "ruecklastschrift"; id: string; rechnungId: string; gebuehrCent: number }): Promise<SepaErgebnis> => {
      if (v.art === "ueberweisung") return rufe<SepaErgebnis>(api.api.laden.sepa.ueberweisung.$post({ json: { ids: v.ids } }));
      if (v.art === "lastschrift") return rufe<SepaErgebnis>(api.api.laden.sepa.lastschrift.$post({ json: { ids: v.ids } }));
      if (v.art === "eingezogen") return rufe<SepaErgebnis>(api.api.laden.sepa[":id"].eingezogen.$post({ param: { id: v.id } }));
      if (v.art === "ruecklastschrift") return rufe<SepaErgebnis>(api.api.laden.sepa[":id"].ruecklastschrift.$post({ param: { id: v.id }, json: { rechnungId: v.rechnungId, gebuehrCent: v.gebuehrCent } }));
      throw new Error("Unbekannte Aktion");
    },
    onSuccess: n,
  });
}
export const sepaDateiUrl = (id: string) => `/api/laden/sepa/${id}/datei`;
export const mahnungPdfUrl = (id: string) => `/api/laden/mahnungen/${id}/pdf`;

export function useMahnAktion() {
  const n = nach();
  return useMutation({
    mutationFn: (v: { art: "lauf" } | { art: "storno"; id: string; grund: string }) => (v.art === "lauf" ? rufe<{ neu: number }>(api.api.laden.mahnungen.lauf.$post()) : rufe(api.api.laden.mahnungen[":id"].storno.$post({ param: { id: v.id }, json: { grund: v.grund } }))),
    onSuccess: n,
  });
}
export function useEinbehalten() {
  const n = nach();
  return useMutation({ mutationFn: (v: { mieterId: string; an: boolean }) => rufe(api.api.laden.mieter[":id"].einbehalten.$post({ param: { id: v.mieterId }, json: { an: v.an } })), onSuccess: n });
}
export function useMandatLaden() {
  const n = nach();
  return useMutation({
    mutationFn: (v: { mieterId: string; art: "anlegen" } | { mieterId: string; art: "widerrufen"; mandatId: string }) => (v.art === "anlegen" ? rufe<{ id: string; referenz: string }>(api.api.laden.mieter[":id"].mandat.$post({ param: { id: v.mieterId } })) : rufe(api.api.laden.mieter[":id"].mandat[":mandatId"].$delete({ param: { id: v.mieterId, mandatId: v.mandatId } }))),
    onSuccess: n,
  });
}
